import React, { useEffect, useState, useCallback } from 'react'
import HomeLoader from '../components/HomeLoader'
import Header from '../components/Header'
import Footer from '../components/Footer';
import Newsletter from '../components/Newsletter';
import InteractiveServices from '../components/InteractiveServices'
import BannerTwo from '../components/BannerTwo';
import ServiceSlider from '../components/ServiceSlider';
import BannerSection from '../components/BannerSection';
import StatsSection from '../components/StatsSection';
import DiagonalServicesSlider from '../components/DiagonalServicesSlider'
import ProcessSection from '../components/ProcessSection';
import FeaturesSection from '../components/FeaturesSection';
import ServicesTransformSection from '../components/ServicesTransformSection';
import TestimonialsSection from '../components/TestimonialsSection';
import ProcessSectionNew from '../components/ProcessSectionNew';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faChevronLeft,
  faQuoteRight,
  faTimes,
  faSearchPlus,
} from "@fortawesome/free-solid-svg-icons";
import CleanupLink from '../components/CleanupLink';
import ContactForm from '../components/ContactForm';

const FaqItem = ({ question, answer, defaultOpen = false }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="accordion-item">
      <h2 className="accordion-header">
        <button
          className={`accordion-button ${open ? '' : 'collapsed'}`}
          type="button"
          onClick={() => setOpen(prev => !prev)}
        >
          {question}
        </button>
      </h2>
      <div className={`accordion-collapse collapse ${open ? 'show' : ''}`}>
        <div className="accordion-body">{answer}</div>
      </div>
    </div>
  );
};

// Gallery images array — unique items only (duplicates are for CSS loop)
const galleryImages = [
  { src: '/images/homeport1.jpg', alt: 'Embroidery Work 1' },
  { src: '/images/homeport2.jpg', alt: 'Embroidery Work 2' },
  { src: '/images/homeport3.jpg', alt: 'Embroidery Work 3' },
  { src: '/images/homeport4.jpg', alt: 'Embroidery Work 4' },
  { src: '/images/homeport5.jpg', alt: 'Embroidery Work 5' },
  { src: '/images/homeport6.jpg', alt: 'Embroidery Work 6' },
];

// Use a rotated second set so no two adjacent loop items are identical.
const loopGalleryImages = [
  ...galleryImages,
  ...galleryImages.slice(1),
  galleryImages[0],
];

export default function Home() {

  // ── Page loader — shown every time homepage mounts ───────────────
  const [loaderDone, setLoaderDone] = useState(false);

  // ── Lightbox state ──────────────────────────────────────────────
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [showContactModal, setShowContactModal] = useState(false);

  const openLightbox = useCallback((index) => {
    setLightbox({ open: true, index });
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox({ open: false, index: 0 });
    document.body.style.overflow = '';
  }, []);

  const prevImage = useCallback(() => {
    setLightbox(prev => ({
      ...prev,
      index: (prev.index - 1 + galleryImages.length) % galleryImages.length,
    }));
  }, []);

  const nextImage = useCallback(() => {
    setLightbox(prev => ({
      ...prev,
      index: (prev.index + 1) % galleryImages.length,
    }));
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!lightbox.open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightbox.open, closeLightbox, prevImage, nextImage]);

  // ── Services slider ─────────────────────────────────────────────
  useEffect(() => {
    const slider = document.getElementById('servicesSlider');
    const prevBtn = document.getElementById('prevBtn');
    const nextBtn = document.getElementById('nextBtn');
    let currentIndex = 0;
    const totalCards = 14;
    const cardsToShow = 3;
    const maxIndex = totalCards - cardsToShow;

    const updateSlider = () => {
      const cardWidth = slider.querySelector('.service-card').offsetWidth;
      const gap = 30;
      const translateX = -(currentIndex * (cardWidth + gap));
      slider.style.transform = `translateX(${translateX}px)`;
      prevBtn.disabled = currentIndex === 0;
      nextBtn.disabled = currentIndex >= maxIndex;
    };

    if (prevBtn && nextBtn && slider) {
      prevBtn.addEventListener('click', () => {
        if (currentIndex > 0) { currentIndex--; updateSlider(); }
      });
      nextBtn.addEventListener('click', () => {
        if (currentIndex < maxIndex) { currentIndex++; updateSlider(); }
      });
      window.addEventListener('resize', updateSlider);
    }

    return () => {
      if (prevBtn && nextBtn) {
        window.removeEventListener('resize', updateSlider);
      }
    };
  }, []);

  // Homepage contact popup: auto-open after a short delay.
  useEffect(() => {
    const hasShown = sessionStorage.getItem('homeContactModalShown');
    if (hasShown) return;

    const timer = window.setTimeout(() => {
      setShowContactModal(true);
      sessionStorage.setItem('homeContactModalShown', '1');
      document.body.style.overflow = 'hidden';
    }, 2500);

    return () => window.clearTimeout(timer);
  }, []);

  // Allow Header "Contact Us" button to open same homepage modal.
  useEffect(() => {
    const handleOpen = () => {
      setShowContactModal(true);
      document.body.style.overflow = 'hidden';
    };

    window.addEventListener('open-home-contact-modal', handleOpen);
    return () => window.removeEventListener('open-home-contact-modal', handleOpen);
  }, []);

  const closeContactModal = useCallback(() => {
    setShowContactModal(false);
    document.body.style.overflow = '';
  }, []);


  return (
    <>

      {/* Loader — rendered every homepage visit, unmounts when done */}
      {!loaderDone && <HomeLoader onDone={() => setLoaderDone(true)} />}

      <Header />

      {showContactModal && (
        <div
          className="home-contact-modal-backdrop"
          onClick={closeContactModal}
          role="dialog"
          aria-modal="true"
          aria-label="Contact form modal"
        >
          <div className="home-contact-modal-panel" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="home-contact-modal-close"
              aria-label="Close contact modal"
              onClick={closeContactModal}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <div className="home-contact-modal-head">
              <h3>Let's Discuss Your Project</h3>
            </div>
            <ContactForm className="home-contact-modal-form" onSubmitted={closeContactModal} />
          </div>
        </div>
      )}

      <BannerSection />

      <ServiceSlider />

      <StatsSection />

      <BannerTwo />

      <ProcessSectionNew />

      <ServicesTransformSection />

      <ProcessSection />

      <DiagonalServicesSlider />

      <section className="case-study-section">
        <div className="container">
          <div className="row mb-5">
            <div className="col-12 text-center">
              <h2 className="section-title">Case Studies</h2>
              <p className="section-subtitle">Discover how we've helped businesses achieve embroidery excellence</p>
            </div>
          </div>

          <div className="row align-items-center mb-5">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <div className="case-study-image">
                <img src="/images/casestudy1.jpg" alt="Custom Uniform Embroidery" />
              </div>
            </div>
            <div className="col-lg-6">
              <div className="case-study-content">
                <span className="case-study-tag">Website Design & Development</span>
                <h3 className="case-study-title">Top Rated Website Development Company in Los Angeles</h3>
                <p className="case-study-description">
                  Grand Indigo Embroidery is a leading website development company in Los Angeles, California, offering professional web design and development services to businesses across the region. We specialize in creating custom websites that are not only visually appealing but also fully optimized for performance and SEO, helping businesses establish a strong online presence and attract more customers.
                </p>
                <div className="case-study-stats">
                  <div className="stat-item-case">
                    <h4>50+</h4>
                    <p>Logo Variations</p>
                  </div>
                  <div className="stat-item-case">
                    <h4>95%</h4>
                    <p>Client Satisfaction</p>
                  </div>
                  <div className="stat-item-case">
                    <h4>72hrs</h4>
                    <p>Project Completion</p>
                  </div>
                </div>
                <CleanupLink to="/contact" className="btn btn-primary">Get In Touch</CleanupLink>
              </div>
            </div>
          </div>

          <div className="row align-items-center mb-5">
            <div className="col-lg-6 order-lg-2 mb-4 mb-lg-0">
              <div className="case-study-image">
                <img src="/images/casestudy2.jpg" alt="Sports Team Digitizing" />
              </div>
            </div>
            <div className="col-lg-6 order-lg-1">
              <div className="case-study-content">
                <span className="case-study-tag">Digitizing Embroidery</span>
                <h3 className="case-study-title">Elevating Sports Team Digitizing Embroidery</h3>
                <p className="case-study-description">
                  A professional sports organization approached us to create eye-catching 3D puff embroidery designs
                  for their team merchandise. We delivered intricate cap digitizing with raised lettering that added
                  dimension and premium quality to their branded apparel, resulting in a 40% increase in merchandise sales.
                </p>
                <div className="case-study-stats">
                  <div className="stat-item-case">
                    <h4>200+</h4>
                    <p>Units Produced</p>
                  </div>
                  <div className="stat-item-case">
                    <h4>40%</h4>
                    <p>Sales Increase</p>
                  </div>
                  <div className="stat-item-case">
                    <h4>48hrs</h4>
                    <p>Turnaround Time</p>
                  </div>
                </div>
                <CleanupLink to="/contact" className="btn btn-primary">Get In Touch</CleanupLink>
              </div>
            </div>
          </div>

          <div className="row align-items-center mb-5">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <div className="case-study-image">
                <img src="/images/casestudy3.jpg" alt="Custom Uniform Embroidery" />
              </div>
            </div>
            <div className="col-lg-6">
              <div className="case-study-content">
                <span className="case-study-tag">Custom Branding</span>
                <h3 className="case-study-title">Transforming Corporate Uniform Identity</h3>
                <p className="case-study-description">
                  We partnered with a leading corporate solutions provider to digitize their entire uniform collection.
                  The project involved creating over 50 unique logo variations for different garment types, ensuring
                  consistent branding across all materials while maintaining exceptional stitch quality and durability.
                </p>
                <div className="case-study-stats">
                  <div className="stat-item-case">
                    <h4>50+</h4>
                    <p>Logo Variations</p>
                  </div>
                  <div className="stat-item-case">
                    <h4>95%</h4>
                    <p>Client Satisfaction</p>
                  </div>
                  <div className="stat-item-case">
                    <h4>72hrs</h4>
                    <p>Project Completion</p>
                  </div>
                </div>
                <CleanupLink to="/contact" className="btn btn-primary">Get In Touch</CleanupLink>
              </div>
            </div>
          </div>

        </div>
      </section>

      <FeaturesSection />

      <InteractiveServices />

      <section className="testimonials-section">
        <div className="container">
          <div className="row mb-5">
            <div className="col-12 text-center">
              <h2 className="section-title">What Our Clients Say</h2>
              <p className="section-subtitle">Trusted by businesses worldwide for quality digitizing services</p>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="testimonials-slider-wrapper position-relative">
                <button className="testimonial-nav-btn testimonial-prev-btn" id="testimonialPrevBtn">
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>

                <div className="testimonials-slider-container">
                  <div className="testimonials-slider" id="testimonialsSlider">

                    <div className="testimonial-card">
                      <div className="quote-icon">
                        <FontAwesomeIcon icon={faQuoteRight} />
                      </div>
                      <div className="testimonial-video">
                        <video controls>
                          <source src="/images/banner-video.mp4" type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                      <div className="testimonial-content">
                        <p className="testimonial-text">
                          "The quality of their embroidery digitizing is exceptional. Every stitch is perfect,
                          and they always deliver on time. Our business has grown significantly since partnering
                          with Grand Indigo. Highly recommend their services!"
                        </p>
                        <h4 className="testimonial-name">John Smith</h4>
                        <p className="testimonial-position">CEO, Fashion Brand Inc.</p>
                      </div>
                    </div>

                    <div className="testimonial-card">
                      <div className="quote-icon">
                        <FontAwesomeIcon icon={faQuoteRight} />
                      </div>
                      <div className="testimonial-video">
                        <video controls>
                          <source src="/images/banner-video.mp4" type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                      <div className="testimonial-content">
                        <p className="testimonial-text">
                          "Outstanding service from start to finish. Their team is professional, responsive,
                          and truly understands our needs. The digitizing quality exceeded our expectations,
                          and the turnaround time was impressive."
                        </p>
                        <h4 className="testimonial-name">Sarah Johnson</h4>
                        <p className="testimonial-position">Owner, Custom Apparel Co.</p>
                      </div>
                    </div>

                    <div className="testimonial-card">
                      <div className="quote-icon">
                        <FontAwesomeIcon icon={faQuoteRight} />
                      </div>
                      <div className="testimonial-video">
                        <video controls>
                          <source src="/images/banner-video.mp4" type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                      <div className="testimonial-content">
                        <p className="testimonial-text">
                          "We've tried several digitizing services, but Grand Indigo stands out. Their attention
                          to detail is remarkable, and they handle complex designs with ease. Our customers love
                          the quality of the final embroidery."
                        </p>
                        <h4 className="testimonial-name">Michael Chen</h4>
                        <p className="testimonial-position">Director, Sports Wear Ltd.</p>
                      </div>
                    </div>

                    <div className="testimonial-card">
                      <div className="quote-icon">
                        <FontAwesomeIcon icon={faQuoteRight} />
                      </div>
                      <div className="testimonial-video">
                        <video controls>
                          <source src="/images/banner-video.mp4" type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                      <div className="testimonial-content">
                        <p className="testimonial-text">
                          "Reliable, professional, and affordable. Grand Indigo has been our go-to digitizing
                          partner for years. They consistently deliver high-quality work that makes our products
                          stand out in the market."
                        </p>
                        <h4 className="testimonial-name">Emily Rodriguez</h4>
                        <p className="testimonial-position">Manager, Uniform Solutions</p>
                      </div>
                    </div>

                    <div className="testimonial-card">
                      <div className="quote-icon">
                        <FontAwesomeIcon icon={faQuoteRight} />
                      </div>
                      <div className="testimonial-video">
                        <video controls>
                          <source src="/images/banner-video.mp4" type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                      <div className="testimonial-content">
                        <p className="testimonial-text">
                          "The best digitizing service we've worked with. They understand our brand perfectly
                          and translate our designs into flawless embroidery files. Communication is excellent,
                          and revisions are handled quickly."
                        </p>
                        <h4 className="testimonial-name">David Williams</h4>
                        <p className="testimonial-position">Creative Director, Logo Experts</p>
                      </div>
                    </div>

                    <div className="testimonial-card">
                      <div className="quote-icon">
                        <FontAwesomeIcon icon={faQuoteRight} />
                      </div>
                      <div className="testimonial-video">
                        <video controls>
                          <source src="/images/banner-video.mp4" type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                      <div className="testimonial-content">
                        <p className="testimonial-text">
                          "Grand Indigo transformed our embroidery business. Their digitizing is top-notch,
                          and they've helped us take on more complex projects with confidence. Customer service
                          is exceptional too!"
                        </p>
                        <h4 className="testimonial-name">Lisa Anderson</h4>
                        <p className="testimonial-position">Founder, Embroidery Plus</p>
                      </div>
                    </div>

                  </div>
                </div>

                <button className="testimonial-nav-btn testimonial-next-btn" id="testimonialNextBtn">
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <TestimonialsSection />

      <section className="faqs-section">
        <div className="container">
          <div className="row mb-5">
            <div className="col-12 text-center">
              <h2 className="section-title">Frequently Asked Questions</h2>
              <p className="section-subtitle">Find answers to common questions about our digitizing services</p>
            </div>
          </div>

          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="accordion" id="faqAccordion">

                <FaqItem
                  defaultOpen={true}
                  question="What is embroidery digitizing and why do I need it?"
                  answer="Embroidery digitizing is the process of converting artwork or logos into a digital embroidery file that embroidery machines can read. This file contains instructions for the machine, including stitch types, directions, and density. You need it because embroidery machines cannot directly read image files like JPG or PNG – they require specialized embroidery formats like DST, PES, or EMB to produce quality embroidered designs."
                />

                <FaqItem
                  question="What file formats do you accept and deliver?"
                  answer="We accept most common image formats including JPG, PNG, PDF, AI, EPS, and PSD. For output, we deliver embroidery files in all major formats including DST, PES, JEF, EXP, XXX, VP3, HUS, EMB, and many more. We also provide the working file in Wilcom or Tajima format along with a preview image so you can see exactly how the design will stitch out."
                />

                <FaqItem
                  question="How long does it take to complete a digitizing order?"
                  answer="Our standard turnaround time is 6-12 hours for regular orders. However, this can vary depending on the complexity of the design and the current workload. We also offer rush services with 3-4 hour delivery for urgent projects. For large or highly complex designs, we may need 24 hours. We always communicate realistic timeframes upfront and keep you updated throughout the process."
                />

                <FaqItem
                  question="Do you offer free revisions if I'm not satisfied?"
                  answer="Yes, absolutely! We offer unlimited free revisions until you are completely satisfied with the final design. Our goal is to ensure the embroidery file works perfectly for your needs. If there are any issues with stitch quality, sizing, or design interpretation, we'll make the necessary adjustments at no additional cost. Your satisfaction is our top priority."
                />

                <FaqItem
                  question="What is the difference between left chest and full back digitizing?"
                  answer="Left chest digitizing is optimized for smaller designs typically placed on the left chest area of shirts, usually ranging from 3.5 to 4 inches wide. These designs use fewer stitches and are perfect for logos and branding. Full back or jacket back digitizing is for larger designs, often 10-12 inches or more, suitable for the back of jackets, hoodies, or shirts. These require different techniques to handle the larger size while maintaining quality and proper stitch density across the entire design."
                />

              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ── Gallery Section ───────────────────────────────────────── */}
      <section className="gallery-section">
        <div className="container">
          <div className="row mb-5">
            <div className="col-12 text-center">
              <h2 className="section-title">Our Work Gallery</h2>
              <p className="section-subtitle">Explore our portfolio of premium embroidery digitizing projects</p>
            </div>
          </div>
        </div>

        <div className="gallery-slider-wrapper">
          <div className="gallery-slider" id="gallerySlider">

            {/* Loop-safe clickable images (no immediate repeats) */}
            {loopGalleryImages.map((img, index) => (
              <div
                key={`${img.src}-${index}`}
                className="gallery-item gallery-item-clickable"
                onClick={() => openLightbox(index % galleryImages.length)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && openLightbox(index % galleryImages.length)}
                aria-label={`Open ${img.alt}`}
              >
                <img src={img.src} alt={img.alt} />
                <div className="gallery-item-overlay">
                  <span className="gallery-zoom-icon">
                    <FontAwesomeIcon icon={faSearchPlus} />
                  </span>
                </div>
              </div>
            ))}

          </div>
        </div>
      </section>

      {/* ── Lightbox Modal ────────────────────────────────────────── */}
      {lightbox.open && (
        <div
          className="gi-lightbox-backdrop"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Image lightbox"
        >
          <div
            className="gi-lightbox-container"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              className="gi-lightbox-close"
              onClick={closeLightbox}
              aria-label="Close lightbox"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>

            {/* Prev */}
            <button
              className="gi-lightbox-nav gi-lightbox-prev"
              onClick={prevImage}
              aria-label="Previous image"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            {/* Image */}
            <div className="gi-lightbox-image-wrap">
              <img
                src={galleryImages[lightbox.index].src}
                alt={galleryImages[lightbox.index].alt}
                className="gi-lightbox-img"
              />
            </div>

            {/* Next */}
            <button
              className="gi-lightbox-nav gi-lightbox-next"
              onClick={nextImage}
              aria-label="Next image"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>

            {/* Counter */}
            <div className="gi-lightbox-counter">
              {lightbox.index + 1} / {galleryImages.length}
            </div>

            {/* Thumbnail strip */}
            <div className="gi-lightbox-thumbs">
              {galleryImages.map((img, i) => (
                <button
                  key={i}
                  className={`gi-lightbox-thumb ${i === lightbox.index ? 'active' : ''}`}
                  onClick={() => setLightbox(prev => ({ ...prev, index: i }))}
                  aria-label={`Go to image ${i + 1}`}
                >
                  <img src={img.src} alt={img.alt} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Lightbox Styles ───────────────────────────────────────── */}
      <style>{`
        /* Clickable gallery item */
          .gallery-item-clickable {
            position: relative !important;
            overflow: hidden !important;
            cursor: pointer;
          }
          .gallery-item-overlay {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            background: rgba(0, 37, 54, 0.55);
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transition: opacity 0.3s ease;
            z-index: 10;
            pointer-events: none;
          }
          .gallery-item-clickable:hover .gallery-item-overlay {
            opacity: 1;
          }
          .gallery-zoom-icon {
            color: #fff;
            font-size: 1.6rem;
            transform: scale(0.7);
            transition: transform 0.3s ease;
          }
          .gallery-item-clickable:hover .gallery-zoom-icon {
            transform: scale(1);
          }

        /* Backdrop */
        .gi-lightbox-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.92);
          z-index: 99999;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: gi-fade-in 0.25s ease;
        }
        @keyframes gi-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        /* Container */
        .gi-lightbox-container {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          max-width: 92vw;
          max-height: 92vh;
        }

        /* Image wrap */
        .gi-lightbox-image-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          animation: gi-zoom-in 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        @keyframes gi-zoom-in {
          from { transform: scale(0.88); opacity: 0; }
          to   { transform: scale(1);    opacity: 1; }
        }
        .gi-lightbox-img {
          max-width: 82vw;
          max-height: 72vh;
          object-fit: contain;
          border-radius: 6px;
          box-shadow: 0 24px 80px rgba(0,0,0,0.7);
          display: block;
        }

        /* Close button */
        .gi-lightbox-close {
          position: absolute;
          top: -44px;
          right: 0;
          background: none;
          border: none;
          color: #fff;
          font-size: 1.4rem;
          cursor: pointer;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: background 0.2s, transform 0.2s;
          z-index: 2;
        }
        .gi-lightbox-close:hover {
          background: rgba(255,255,255,0.15);
          transform: rotate(90deg);
        }

        /* Prev / Next */
        .gi-lightbox-nav {
          position: fixed;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.2);
          color: #fff;
          font-size: 1.1rem;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s, transform 0.2s;
          z-index: 2;
        }
        .gi-lightbox-nav:hover {
          background: rgba(0, 168, 204, 0.6);
          transform: translateY(-50%) scale(1.08);
        }
        .gi-lightbox-prev { left: 16px; }
        .gi-lightbox-next { right: 16px; }

        /* Counter */
        .gi-lightbox-counter {
          color: rgba(255,255,255,0.6);
          font-size: 0.8rem;
          letter-spacing: 0.08em;
          margin-top: 14px;
        }

        /* Thumbnail strip */
        .gi-lightbox-thumbs {
          display: flex;
          gap: 8px;
          margin-top: 12px;
          overflow-x: auto;
          max-width: 82vw;
          padding-bottom: 4px;
        }
        .gi-lightbox-thumbs::-webkit-scrollbar { height: 3px; }
        .gi-lightbox-thumbs::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 2px; }
        .gi-lightbox-thumb {
          flex-shrink: 0;
          width: 60px;
          height: 44px;
          border-radius: 4px;
          overflow: hidden;
          border: 2px solid transparent;
          background: none;
          padding: 0;
          cursor: pointer;
          opacity: 0.5;
          transition: opacity 0.2s, border-color 0.2s;
        }
        .gi-lightbox-thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .gi-lightbox-thumb.active,
        .gi-lightbox-thumb:hover {
          opacity: 1;
          border-color: #00a8cc;
        }

        /* Mobile adjustments */
        @media (max-width: 576px) {
          .gi-lightbox-img { max-width: 95vw; max-height: 60vh; }
          .gi-lightbox-prev { left: 6px; }
          .gi-lightbox-next { right: 6px; }
          .gi-lightbox-nav { width: 38px; height: 38px; font-size: 0.9rem; }
        }
      `}</style>

      <Newsletter />

      <Footer />
    </>
  )
}